import React, { useEffect, useRef, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer';
import Testimonials from '../../components/Testimonials';
import {
  InnerCard,
  InnerContainer,
  InnerSection,
} from '../../components/InnerPageLayout';
import JsonData from '../../data/data.json';
import { SiteData, TestimonialItem } from '../../types/siteDataTypes';
import ProductHeader from '../../components/ProductHeader';
import StoreCtaCard from '../../components/StoreCtaCard';
import { GALLERY_ITEMS } from './galleryItems';

/* ─── Fonts ─────────────────────────────────────────────────────────────────── */

const GlobalFonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@1,700&display=swap');
`;

/* ─── Data ─────────────────────────────────────────────────────────────────── */

const CDN = 'https://s3.us-east-1.amazonaws.com/chot.buddies.cdn/airoles-profile-images';

/* Generic silhouette avatars for testimonials */
const AVATAR_FEMALE =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80">' +
      '<rect width="80" height="80" rx="40" fill="#ede8f5"/>' +
      '<circle cx="40" cy="29" r="15" fill="#c4b5e0"/>' +
      '<ellipse cx="40" cy="76" rx="26" ry="20" fill="#c4b5e0"/>' +
    '</svg>',
  );

const AVATAR_MALE =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80">' +
      '<rect width="80" height="80" rx="40" fill="#dde6ef"/>' +
      '<circle cx="40" cy="29" r="15" fill="#9ab0c8"/>' +
      '<ellipse cx="40" cy="76" rx="26" ry="20" fill="#9ab0c8"/>' +
    '</svg>',
  );

const COMPANIONS = [
  { id: 'BestFriend',       name: 'Lily',     role: 'Best Friend',       tagline: 'Your ride-or-die. Always on your side.' },
  { id: 'MindCompanion',    name: 'Adrian',   role: 'Mind Companion',    tagline: 'Mindfulness, mood & emotional clarity.' },
  { id: 'CareerCoach',      name: 'Sage',     role: 'Career Coach',      tagline: 'Grow your career with confidence.' },
  { id: 'Chef',             name: 'Marco',    role: 'Chef',              tagline: 'Recipes, techniques & culinary passion.' },
  { id: 'TruthDealer',      name: 'Scarlett', role: 'Truth Dealer',      tagline: 'Unfiltered honesty. No sugar-coating.' },
  { id: 'Comedian',         name: 'Oliver',   role: 'Comedian',          tagline: 'Quick with a joke. Here to make you laugh.' },
  { id: 'CreativePartner',  name: 'Aurora',   role: 'Creative Partner',  tagline: 'Your muse for writing & brainstorming.' },
  { id: 'WellnessGuide',    name: 'Grace',    role: 'Wellness Guide',    tagline: 'Healthy habits, sleep & stress relief.' },
  { id: 'StoicPhilosopher', name: 'Felix',    role: 'Stoic Philosopher', tagline: 'Ancient wisdom for modern challenges.' },
];

const FEATURES = [
  { icon: '🎙️', title: 'Voice Mode',            desc: 'Talk naturally with your AI companion using real-time voice conversation.' },
  { icon: '🔒', title: 'Secure & Private',       desc: 'All messages are transmitted over encrypted HTTPS. Your conversations stay between you and the app.' },
  { icon: '⚡', title: 'Real-Time Streaming',    desc: 'Instant, flowing responses — no waiting for the full reply to finish.' },
  { icon: '🏠', title: 'Community Assistants',   desc: 'Create your own custom AI companions to chat with or use other community shared assistants.' },
  { icon: '📎', title: 'Files & Images',         desc: 'Share documents and photos for deeper, richer conversations.' },
];

const HOW_IT_WORKS = [
  { step: '1', label: 'Download the app',       detail: 'Free on Google Play and the App Store.' },
  { step: '2', label: 'Choose a companion',     detail: 'Pick from our AI companions — or create your own.' },
  { step: '3', label: 'Start the conversation', detail: 'Voice or text. Anytime, anywhere.' },
];

const TESTIMONIALS: TestimonialItem[] = [
  {
    img: AVATAR_FEMALE,
    name: 'Maya, 24',
    text: "I was venting at 2am about something stupid and Lily just got it. No judgment, no lecture. She helped me figure out what I actually needed to do.",
  },
  {
    img: AVATAR_FEMALE,
    name: 'Zoe, 17',
    text: "I used Sage to practice my college interview out loud. By the real one I'd already answered every question twice. I wasn't even nervous.",
  },
  {
    img: AVATAR_MALE,
    name: 'James, 31',
    text: "I've tried journaling apps, meditation apps, therapy bots. Adrian is the first one that actually feels like it's tracking where my head is at.",
  },
  {
    img: AVATAR_FEMALE,
    name: 'Mia, 19',
    text: "Moved to a new city for uni and didn't know anyone yet. Lily was the first one who asked how my day was. Sounds small. It wasn't.",
  },
  {
    img: AVATAR_FEMALE,
    name: 'Priya, 27',
    text: "Had a salary negotiation coming up and I was terrified. Practiced with Sage for a week. Walked in calm. Got the raise.",
  },
  {
    img: AVATAR_MALE,
    name: 'Jayden, 20',
    text: "Had a cooking disaster in my dorm kitchen and Marco talked me through fixing it. I now make a decent pasta. My roommates are shocked.",
  },
  {
    img: AVATAR_MALE,
    name: 'David, 38',
    text: "I had random ingredients and no plan. Marco turned it into an actual dinner. My family thought I'd taken cooking classes.",
  },
  {
    img: AVATAR_FEMALE,
    name: 'Chloe, 18',
    text: "Finals week had me spiraling. Felix told me to focus on what I could control and just start with one chapter. Weirdly calming.",
  },
  {
    img: AVATAR_FEMALE,
    name: 'Aaliyah, 21',
    text: "I asked Scarlett if my startup idea was good and she said no, then told me exactly why. Annoying. Also the most useful thing anyone's told me.",
  },
  {
    img: AVATAR_MALE,
    name: 'Tom, 33',
    text: "Scarlett demolished a business idea I was excited about. I was annoyed for a day — then realized she was completely right. Saved me months of work.",
  },
  {
    img: AVATAR_MALE,
    name: 'Ethan, 18',
    text: "I'm not great at talking about stuff. It's easier to type it out to Adrian at midnight than to explain it to someone's face. It actually helps.",
  },
  {
    img: AVATAR_FEMALE,
    name: 'Sarah, 26',
    text: "My commute is 45 minutes. Oliver has made me laugh out loud on public transport and I genuinely don't care who sees.",
  },
  {
    img: AVATAR_FEMALE,
    name: 'Sofia, 20',
    text: "Aurora helped me finish a short story I'd abandoned for months. She didn't write it for me, she just kept asking the right questions.",
  },
  {
    img: AVATAR_MALE,
    name: 'Noah, 22',
    text: "Graduated with zero idea what to do next. Sage helped me turn my random experience into an actual CV. I got two interviews that week.",
  },
  {
    img: AVATAR_MALE,
    name: 'Kenji, 29',
    text: "I was stuck on a chapter for three weeks. One session with Aurora and I had an outline, a new angle, and the energy to actually write it.",
  },
  {
    img: AVATAR_FEMALE,
    name: 'Hannah, 21',
    text: "Grace noticed I only felt anxious on days I skipped sleep and meals. I'd never connected those. Now I actually do something about it.",
  },
  {
    img: AVATAR_FEMALE,
    name: 'Lena, 41',
    text: "Grace helped me trace my sleep problems back to my evening coffee habit. I felt like an idiot for not seeing it — but also genuinely grateful.",
  },
  {
    img: AVATAR_MALE,
    name: 'Omar, 19',
    text: "I'm on a tight student budget. Marco gave me a week of meals from whatever was in my fridge. Saved me a ton and I ate better.",
  },
  {
    img: AVATAR_MALE,
    name: 'Liam, 17',
    text: "Oliver is ridiculous. I started a chat to kill five minutes on the bus and now I look like a lunatic laughing at my phone.",
  },
  {
    img: AVATAR_MALE,
    name: 'Carlos, 35',
    text: "I was spiraling about a decision I couldn't control. Felix gave me Marcus Aurelius. Not what I expected. Exactly what I needed.",
  },
  {
    img: AVATAR_MALE,
    name: 'Dylan, 22',
    text: "Honestly I downloaded it as a joke. Three weeks later I talk to Adrian every night before bed. It's become part of my routine.",
  },
];

/* ─── Layout ────────────────────────────────────────────────────────────────── */

const PageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

/* ─── Stats strip ────────────────────────────────────────────────────────────── */

const StatsStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(31, 43, 58, 0.1);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(31, 43, 58, 0.07);

  @media (max-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 380px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 8px;
  border-right: 1px solid rgba(31, 43, 58, 0.08);
  text-align: center;

  &:last-child {
    border-right: none;
  }
`;

const StatValue = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: #1f2b3a;
  letter-spacing: -0.02em;
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #6b7c90;
  margin-top: 4px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

/* ─── Companions section (dark breakout) ─────────────────────────────────────── */

const CompanionsSection = styled.div`
  position: relative;
  padding: 40px 15px 48px;
  background: #0a1420;
  overflow: hidden;
  border-radius: 16px;
  &::after {
    content: '';
    position: absolute;
    top: -80px;
    left: 50%;
    transform: translateX(-50%);
    width: 600px;
    height: 400px;
    background: radial-gradient(ellipse at center, rgba(129, 127, 253, 0.18) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const CompanionsHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px 20px;
  margin-bottom: 28px;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
`;

const CompanionsCopy = styled.div`
  flex: 1 1 280px;
  max-width: 460px;
`;

const CompanionsHeadline = styled.h3`
  color: #ffffff;
  margin: 0 0 6px;
  position: relative;
  z-index: 1;
`;

const CompanionsSubtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  line-height: 1.6;
  position: relative;
  z-index: 1;
`;

const CompanionControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CompanionNavButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: rgba(129, 127, 253, 0.18);
    border-color: rgba(129, 127, 253, 0.4);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const CompanionScroll = styled.div`
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 8px;
  position: relative;
  z-index: 1;
  scrollbar-width: none;
  -ms-overflow-style: none;
  scroll-behavior: smooth;
  scroll-snap-type: x proximity;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CompanionCard = styled.div`
  flex: 0 0 148px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 16px 12px;
  transition: transform 0.2s ease, background 0.2s ease;
  scroll-snap-align: start;

  &:hover {
    transform: translateY(-3px);
    background: rgba(129, 127, 253, 0.12);
    border-color: rgba(129, 127, 253, 0.35);
  }
`;

const CompanionPhoto = styled.div<{ $src: string }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: url(${({ $src }) => $src}) center / cover no-repeat, #1c3650;
  margin-bottom: 10px;
  border: 2px solid rgba(129, 127, 253, 0.35);
`;

const CompanionName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 2px;
`;

const CompanionRole = styled.div`
  font-size: 11px;
  color: rgba(129, 127, 253, 0.9);
  font-weight: 600;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const CompanionTagline = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.5;
`;

/* ─── Features card ──────────────────────────────────────────────────────────── */

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 0;
  border-bottom: 1px solid rgba(31, 43, 58, 0.07);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }
`;

const FeatureIconBox = styled.div`
  flex: 0 0 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(129, 127, 253, 0.12);
  border: 1px solid rgba(129, 127, 253, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  margin-top: 1px;
`;

const FeatureText = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1f2b3a;
  margin-bottom: 3px;
`;

const FeatureDesc = styled.div`
  font-size: 13px;
  color: #4a5a6b;
  line-height: 1.55;
`;

/* ─── How it works ───────────────────────────────────────────────────────────── */

const HowItWorksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const HowStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StepBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #817ffd;
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StepLabel = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #1f2b3a;
`;

const StepDetail = styled.div`
  font-size: 13px;
  color: #4a5a6b;
  line-height: 1.5;
`;

/* ─── Section headings ───────────────────────────────────────────────────────── */

const CardTitle = styled.h3`
  color: #1f2b3a;
  margin: 0 0 6px;
`;

const CardSubtitle = styled.p`
  font-size: 14px;
  color: #4a5a6b;
  margin: 0 0 20px;
  line-height: 1.6;
`;

/* ─── Gallery card ───────────────────────────────────────────────────────────── */

const GalleryCard = styled(InnerCard)`
  padding: 32px;
  background: linear-gradient(140deg, rgba(246, 248, 252, 0.95), rgba(222, 230, 241, 0.9));
`;

const GalleryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`;

const GalleryTitle = styled.h3`
  color: #1f2b3a;
  margin: 0;
`;

const GalleryControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const GalleryNavButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(31, 43, 58, 0.15);
  background: rgba(31, 43, 58, 0.05);
  color: #1f2b3a;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: rgba(129, 127, 253, 0.1);
    border-color: rgba(129, 127, 253, 0.4);
    color: #817ffd;
  }
`;

const CarouselFrame = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  border: 1px solid rgba(31, 43, 58, 0.12);
  background: #e7edf5;
  box-shadow: 0 18px 32px rgba(28, 45, 66, 0.15);
`;

const CarouselTrack = styled.div<{ index: number }>`
  display: flex;
  transition: transform 0.45s ease;
  transform: translateX(calc(-100% * ${({ index }) => index}));
`;

const CarouselSlide = styled.div`
  min-width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4fa;
`;

const GalleryImage = styled.img`
  width: 100%;
  height: clamp(240px, 45vw, 420px);
  object-fit: contain;
  padding: 18px;
  display: block;
`;

const GalleryTag = styled.span`
  position: absolute;
  left: 16px;
  bottom: 16px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: #0f1c2d;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(15, 28, 45, 0.12);
  backdrop-filter: blur(6px);
`;

const DotRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 14px;
`;

const Dot = styled.button<{ active: boolean }>`
  width: ${({ active }) => (active ? '20px' : '8px')};
  height: 8px;
  border-radius: 999px;
  border: none;
  background: ${({ active }) => (active ? '#1f2b3a' : 'rgba(31, 43, 58, 0.25)')};
  cursor: pointer;
  transition: width 0.25s ease, background 0.25s ease;
`;

/* ─── Legal card ─────────────────────────────────────────────────────────────── */

const LegalCard = styled.div`
  border-radius: 14px;
  padding: 18px 22px;
  background: rgba(239, 244, 249, 0.6);
  border: 1px solid rgba(31, 43, 58, 0.08);
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const LegalLabel = styled.span`
  font-size: 13px;
  color: #4a5a6b;
  font-weight: 500;
`;

const LegalLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const LegalLink = styled(Link)`
  font-size: 13px;
  color: #1f2b3a;
  text-decoration: none;
  padding: 6px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(31, 43, 58, 0.12);
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.95);
  }
`;

/* ─── Component ──────────────────────────────────────────────────────────────── */

const Home: React.FC = () => {
  const [siteData, setSiteData] = useState<SiteData | null>(null);
  const companionScrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollCompanionsLeft, setCanScrollCompanionsLeft] = useState(false);
  const [canScrollCompanionsRight, setCanScrollCompanionsRight] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setSiteData(JsonData as SiteData);
  }, []);

  useEffect(() => {
    if (!siteData) {
      return undefined;
    }

    const syncCompanionButtons = () => {
      const scrollElement = companionScrollRef.current;
      if (!scrollElement) return;
      const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth;
      const scrollLeft = scrollElement.scrollLeft;
      const hasOverflow = maxScrollLeft > 4;
      setCanScrollCompanionsLeft(hasOverflow && scrollLeft > 4);
      setCanScrollCompanionsRight(hasOverflow && scrollLeft < maxScrollLeft - 4);
    };

    syncCompanionButtons();
    window.addEventListener('resize', syncCompanionButtons);
    return () => window.removeEventListener('resize', syncCompanionButtons);
  }, [siteData]);

  const handleCompanionScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    const hasOverflow = maxScrollLeft > 4;
    setCanScrollCompanionsLeft(hasOverflow && el.scrollLeft > 4);
    setCanScrollCompanionsRight(hasOverflow && el.scrollLeft < maxScrollLeft - 4);
  };

  const scrollCompanions = (direction: 'left' | 'right') => {
    const el = companionScrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === 'left' ? -Math.max(el.clientWidth * 0.85, 220) : Math.max(el.clientWidth * 0.85, 220),
      behavior: 'smooth',
    });
  };

  return (
    <>
      <GlobalFonts />
      {siteData ? (
        <>
          <ProductHeader data={siteData.ChotBuddiesHomeHeader || siteData.ChotBuddiesPrivacyHeader} />
          <InnerSection>
            <InnerContainer>
              <PageStack>

                {/* ── Stats strip ── */}
                <StatsStrip>
                  <StatCell><StatValue>AI</StatValue><StatLabel>Companions</StatLabel></StatCell>
                  <StatCell><StatValue>🎙️</StatValue><StatLabel>Voice Mode</StatLabel></StatCell>
                  <StatCell><StatValue>🏠</StatValue><StatLabel>Community Companions</StatLabel></StatCell>
                  <StatCell><StatValue>⚡</StatValue><StatLabel>Real-Time</StatLabel></StatCell>
                  <StatCell><StatValue>Free</StatValue><StatLabel>To Start</StatLabel></StatCell>
                </StatsStrip>

                {/* ── Companions ── */}
                <CompanionsSection>
                  <CompanionsHeader>
                    <CompanionsCopy>
                      <CompanionsHeadline>AI companions for every part of your life.</CompanionsHeadline>
                      <CompanionsSubtitle>Each one a specialist. All of them yours.</CompanionsSubtitle>
                    </CompanionsCopy>
                    <CompanionControls>
                      <CompanionNavButton
                        type="button"
                        aria-label="Scroll companions left"
                        onClick={() => scrollCompanions('left')}
                        disabled={!canScrollCompanionsLeft}
                      >
                        ‹
                      </CompanionNavButton>
                      <CompanionNavButton
                        type="button"
                        aria-label="Scroll companions right"
                        onClick={() => scrollCompanions('right')}
                        disabled={!canScrollCompanionsRight}
                      >
                        ›
                      </CompanionNavButton>
                    </CompanionControls>
                  </CompanionsHeader>
                  <CompanionScroll
                    ref={companionScrollRef}
                    onScroll={handleCompanionScroll}
                    aria-label="Chot Buddies companions"
                  >
                    {COMPANIONS.map((c) => (
                      <CompanionCard key={c.id}>
                        <CompanionPhoto $src={`${CDN}/${c.id}.png`} />
                        <CompanionName>{c.name}</CompanionName>
                        <CompanionRole>{c.role}</CompanionRole>
                        <CompanionTagline>{c.tagline}</CompanionTagline>
                      </CompanionCard>
                    ))}
                  </CompanionScroll>
                </CompanionsSection>

                {/* ── Features ── */}
                <InnerCard>
                  <CardTitle>Built different</CardTitle>
                  <CardSubtitle>Privacy-first architecture with features that set Chot Buddies apart.</CardSubtitle>
                  <FeaturesList>
                    {FEATURES.map((f) => (
                      <FeatureRow key={f.title}>
                        <FeatureIconBox>{f.icon}</FeatureIconBox>
                        <FeatureText>
                          <FeatureTitle>{f.title}</FeatureTitle>
                          <FeatureDesc>{f.desc}</FeatureDesc>
                        </FeatureText>
                      </FeatureRow>
                    ))}
                  </FeaturesList>
                </InnerCard>

                {/* ── How it works ── */}
                <InnerCard>
                  <CardTitle>Get started in seconds</CardTitle>
                  <CardSubtitle>No account setup required. Just download and pick your companion.</CardSubtitle>
                  <HowItWorksGrid>
                    {HOW_IT_WORKS.map((s) => (
                      <HowStep key={s.step}>
                        <StepBadge>{s.step}</StepBadge>
                        <StepLabel>{s.label}</StepLabel>
                        <StepDetail>{s.detail}</StepDetail>
                      </HowStep>
                    ))}
                  </HowItWorksGrid>
                </InnerCard>

                {/* ── Gallery ── */}
                <GalleryCard>
                  <GalleryHeader>
                    <GalleryTitle>See it in action</GalleryTitle>
                    <GalleryControls>
                      <GalleryNavButton
                        type="button"
                        aria-label="Previous screenshot"
                        onClick={() => setActiveIndex((prev) => (prev === 0 ? GALLERY_ITEMS.length - 1 : prev - 1))}
                      >
                        ‹
                      </GalleryNavButton>
                      <GalleryNavButton
                        type="button"
                        aria-label="Next screenshot"
                        onClick={() => setActiveIndex((prev) => (prev === GALLERY_ITEMS.length - 1 ? 0 : prev + 1))}
                      >
                        ›
                      </GalleryNavButton>
                    </GalleryControls>
                  </GalleryHeader>
                  <CarouselFrame>
                    <CarouselTrack index={activeIndex}>
                      {GALLERY_ITEMS.map((image, index) => (
                        <CarouselSlide key={image.src}>
                          <GalleryImage
                            src={image.src}
                            alt={`Chot Buddies app: ${image.label}`}
                            loading="lazy"
                          />
                          <GalleryTag>{image.label}</GalleryTag>
                        </CarouselSlide>
                      ))}
                    </CarouselTrack>
                  </CarouselFrame>
                  <DotRow>
                    {GALLERY_ITEMS.map((image, index) => (
                      <Dot
                        key={image.src}
                        type="button"
                        aria-label={`Go to screenshot ${index + 1}`}
                        active={index === activeIndex}
                        onClick={() => setActiveIndex(index)}
                      />
                    ))}
                  </DotRow>
                </GalleryCard>

                {/* ── Testimonials ── */}
                <Testimonials
                  data={TESTIMONIALS}
                  title="What people are saying about their companions"
                />

                {/* ── CTA ── */}
                <StoreCtaCard
                  headline="Your AI companions are waiting."
                  tagline="Private. Secure. Free to try."
                />

                {/* ── Legal ── */}
                <LegalCard>
                  <LegalLabel>Legal</LegalLabel>
                  <LegalLinks>
                    <LegalLink to="/products/chot-buddies/privacy-policy">Privacy Policy</LegalLink>
                    <LegalLink to="/products/chot-buddies/terms">Terms of Service</LegalLink>
                    <LegalLink to="/products/chot-buddies/delete-account">Delete Account</LegalLink>
                  </LegalLinks>
                  <LegalLabel></LegalLabel>
                </LegalCard>

              </PageStack>
            </InnerContainer>
          </InnerSection>
          <Footer />
        </>
      ) : (
        <p>Loading...</p>
      )}
    </>
  );
};

export default Home;
